import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Code2, FileText, Check, Trash } from 'lucide-react';
import Together from 'together-ai';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AIChatAssistantProps {
  code: string;
  challenge: {
    title: string;
    description: string;
    difficulty: string;
  };
  testResults: any[];
}

interface CodeProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const client = new Together({
  apiKey: import.meta.env.VITE_TOGETHER_API_KEY,
});

export default function AIChatAssistant({ code, challenge, testResults }: AIChatAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [editorType, setEditorType] = useState<'code' | 'note'>('code');
  const [formattedContents, setFormattedContents] = useState<FormattedContent[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      generateInitialFeedback();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateInitialFeedback = async () => {
    setIsLoading(true);
    try {
      const passedTests = testResults.filter(r => r.passed).length;
      const totalTests = testResults.length;
      
      const prompt = `Analyze this coding challenge attempt and provide detailed feedback using markdown formatting:

Challenge: ${challenge.title}
Difficulty: ${challenge.difficulty}
Description: ${challenge.description}
Test Results: Passed ${passedTests}/${totalTests} tests

Student's Code:
\`\`\`csharp
${code}
\`\`\`

Provide a detailed analysis using this markdown format:

# Analysis and Feedback

## What You Did Well
- Point 1
- Point 2

## Areas for Improvement
- Area 1
- Area 2

## Key Concepts to Study
1. Concept 1
   - Sub-point
   - Sub-point
2. Concept 2
   - Sub-point
   - Sub-point

## Code Examples

Here's an improved version of your solution:

\`\`\`csharp
// Example code here
\`\`\`

## Learning Resources
- [Resource 1](url)
- [Resource 2](url)

## Next Steps
1. Step 1
2. Step 2

Remember to use **bold** for emphasis and include proper code blocks with syntax highlighting.`;

      const response = await client.chat.completions.create({
        model: 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful C# programming mentor. Provide detailed, well-formatted feedback using markdown.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7
      });

      const feedback = response.choices[0]?.message?.content || 'Unable to generate feedback';
      setMessages([{ role: 'assistant', content: feedback }]);
    } catch (error) {
      console.error('Error generating feedback:', error);
      setMessages([{ 
        role: 'assistant', 
        content: '# Error\nI encountered an error while analyzing your code. Please try asking me a specific question!' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditor = (type: 'code' | 'note') => {
    setEditorType(type);
    setEditorContent(type === 'code' ? '// Your code here' : 'Your note here');
    setShowEditorModal(true);
  };

  const confirmEditor = () => {
    const id = Math.random().toString(36).substr(2, 9);
    const newContent: FormattedContent = {
      type: editorType,
      content: editorContent,
      id
    };
    setFormattedContents(prev => [...prev, newContent]);
    setShowEditorModal(false);
    setEditorContent('');
  };

  const removeFormattedContent = (id: string) => {
    setFormattedContents(prev => prev.filter(content => content.id !== id));
  };

  const getFormattedContentPreview = (content: FormattedContent) => {
    if (content.type === 'code') {
      return `[Code Block - ${content.content.split('\n')[0]}...]`;
    }
    return `[Note - ${content.content.substring(0, 30)}${content.content.length > 30 ? '...' : ''}]`;
  };

  interface FormattedContent {
    type: 'code' | 'note';
    content: string;
    id: string;
  }

  const components: Components = {
    code({ node, inline, className, children, ...props }: CodeProps) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          language={match[1]}
          PreTag="div"
          {...props}
          style={vscDarkPlus}
          className="rounded-md"
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    blockquote({ node, children, ...props }) {
      // Check if this is our special note format
      const isNote = String(children).startsWith('**Note:**');
      if (isNote) {
        return (
          <div className="my-4 relative bg-yellow-100 p-6 rounded-lg shadow-md transform rotate-[0.5deg] border-t-8 border-yellow-300"
               style={{
                 background: 'linear-gradient(to bottom right, #fff9c4, #fff59d)',
                 boxShadow: '2px 3px 7px rgba(0,0,0,0.2)',
               }}>
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-8 h-3 bg-yellow-300/50 rounded-sm"></div>
            <div className="font-handwriting text-gray-800">
              {String(children).replace('**Note:**', '').trim()}
            </div>
          </div>
        );
      }
      return <blockquote {...props}>{children}</blockquote>;
    },
    strong({ node, children, ...props }) {
      return (
        <strong className="font-semibold text-gray-900" {...props}>
          {children}
        </strong>
      );
    },
    h1: ({ children, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
      <h1 className="text-xl font-bold text-gray-800 mb-4" {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }: React.ComponentPropsWithoutRef<'h2'>) => (
      <h2 className="text-lg font-semibold text-gray-800 mb-3 mt-4" {...props}>
        {children}
      </h2>
    ),
    p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
      <p className="text-gray-700 mb-2" {...props}>
        {children}
      </p>
    ),
    ul: ({ children, ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
      <ul className="list-disc list-inside mb-3 space-y-1" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
      <ol className="list-decimal list-inside mb-3 space-y-1" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => (
      <li className="text-gray-700" {...props}>
        {children}
      </li>
    ),
    a: ({ href, children, ...props }: React.ComponentPropsWithoutRef<'a'>) => (
      <a 
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-purple-600 hover:text-purple-700 underline"
        {...props}
      >
        {children}
      </a>
    ),
  };

  const renderMessage = (content: string) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );

  const insertFormat = (format: string) => {
    switch (format) {
      case 'code':
        setNewMessage(prev => `${prev}\n\`\`\`csharp\n// Your code here\n\`\`\`\n`);
        break;
      case 'note':
        setNewMessage(prev => `${prev}\n> **Note:**\n> Your note here\n`);
        break;
      default:
        break;
    }
    setShowFormatting(false);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && formattedContents.length === 0) || isLoading) return;

    let fullMessage = newMessage;

    // Add formatted contents to the message
    if (formattedContents.length > 0) {
      formattedContents.forEach(content => {
        if (content.type === 'code') {
          fullMessage += `\n\n\`\`\`csharp\n${content.content}\n\`\`\``;
        } else {
          fullMessage += `\n\n> **Note:**\n> ${content.content}`;
        }
      });
    }

    setNewMessage('');
    setFormattedContents([]); // Clear formatted contents after sending
    setMessages(prev => [...prev, { role: 'user', content: fullMessage }]);
    setIsLoading(true);

    try {
      const response = await client.chat.completions.create({
        model: 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful C# programming mentor. Provide clear, concise answers focused on helping the student learn.'
          },
          ...messages.map(m => ({ role: m.role, content: m.content })),
          { role: 'user', content: fullMessage }
        ],
        temperature: 0.7
      });

      const reply = response.choices[0]?.message?.content || 'I apologize, I had trouble generating a response.';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try asking your question again!' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-purple-600 text-white p-4 rounded-full shadow-lg hover:bg-purple-700 transition-colors"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {showEditorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {editorType === 'code' ? (
            <div className="relative bg-[#282c34] text-white rounded-lg shadow-xl w-[700px] overflow-hidden border border-gray-700">
              {/* Window Controls */}
              <div className="bg-[#21252b] px-3 py-2.5 flex items-center border-b border-gray-700">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f56] shadow-sm"></div>
                  <div className="w-3 h-3 rounded-full bg-[#ffbd2e] shadow-sm"></div>
                  <div className="w-3 h-3 rounded-full bg-[#27c93f] shadow-sm"></div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 text-gray-400 text-sm font-mono">
                  Program.cs
                </div>
              </div>

              <div className="relative flex min-h-[400px]">
                {/* Line Numbers */}
                <div 
                  className="pl-4 pr-3 py-4 text-right text-gray-500/70 select-none bg-[#21252b] font-mono text-sm border-r border-gray-700/50" 
                  style={{ minWidth: '50px' }}
                >
                  {Array.from({ length: Math.max(editorContent.split('\n').length, 10) }, (_, i) => (
                    <div key={i + 1} className="leading-6 text-xs">
                      {i + 1}
                    </div>
                  ))}
                </div>

                {/* Code Editor */}
                <div className="flex-1 relative">
                  <textarea
                    value={editorContent}
                    onChange={(e) => {
                      setEditorContent(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.max(e.target.scrollHeight, 400)}px`;
                    }}
                    className="w-full px-4 py-4 bg-[#282c34] border-none font-mono text-sm text-[#abb2bf] focus:outline-none resize-none min-h-[400px]"
                    placeholder="// Enter your code here"
                    style={{
                      fontFamily: 'JetBrains Mono, Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                      lineHeight: '1.5',
                      caretColor: '#fff',
                    }}
                    spellCheck="false"
                  />
                </div>
              </div>

              {/* Status Bar */}
              <div className="flex justify-between items-center px-4 py-2 bg-[#21252b] border-t border-gray-700/50 text-xs font-mono">
                <div className="text-gray-400">
                  <span className="text-blue-400">C#</span> · spaces: 4 · UTF-8
                </div>
                <button
                  onClick={confirmEditor}
                  className="flex items-center gap-1.5 text-gray-300 hover:text-white transition-colors group"
                >
                  <span className="text-blue-400 group-hover:text-blue-300">{`public`}</span>
                  <span className="text-blue-400 group-hover:text-blue-300">{`void`}</span>
                  <span>{`Confirm`}</span>
                  <span className="text-purple-400 group-hover:text-purple-300">{`()`}</span>
                  <span className="text-yellow-300">{`{`}</span>
                  <span className="text-green-400 group-hover:text-green-300">{`Save();`}</span>
                  <span className="text-yellow-300">{`}`}</span>
                </button>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowEditorModal(false)}
                className="absolute top-2.5 right-3 text-gray-400 hover:text-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="relative w-[400px]">
              <div className="relative bg-yellow-100 w-full p-6 rounded-lg shadow-md transform rotate-[0.5deg] min-h-[300px]"
                   style={{
                     background: 'linear-gradient(to bottom right, #fff9c4, #fff59d)',
                     boxShadow: '3px 4px 10px rgba(0,0,0,0.2)',
                   }}>
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-8 h-3 bg-yellow-300/50 rounded-sm"></div>
                
                <button
                  onClick={() => setShowEditorModal(false)}
                  className="absolute top-2 right-2 text-gray-600 hover:text-red-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <textarea
                  value={editorContent}
                  onChange={(e) => setEditorContent(e.target.value)}
                  className="w-full h-[200px] bg-transparent border-none resize-none focus:outline-none font-handwriting text-gray-800"
                  placeholder="Enter your note here"
                  style={{
                    background: 'repeating-linear-gradient(transparent, transparent 30px, #eedb7b 31px)',
                    lineHeight: '31px',
                    paddingTop: '0',
                  }}
                />

                <div className="absolute bottom-4 right-4">
                  <button
                    onClick={confirmEditor}
                    className="bg-yellow-500/80 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {isOpen && (
        <div className="fixed bottom-24 right-6 w-[32rem] bg-white rounded-lg shadow-xl border border-gray-200">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="font-semibold text-gray-800">AI Learning Assistant</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="h-[32rem] overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[90%] p-4 rounded-lg ${
                    message.role === 'assistant'
                      ? 'bg-gray-50 text-gray-800'
                      : 'bg-purple-600 text-white'
                  }`}
                >
                  {message.role === 'assistant' 
                    ? renderMessage(message.content)
                    : message.content
                  }
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="p-4 border-t">
            <div className="flex flex-col gap-2">
              <div className="flex gap-1 mb-2">
                <button
                  type="button"
                  onClick={() => openEditor('code')}
                  className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  title="Add Code Block"
                >
                  <Code2 className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => openEditor('note')}
                  className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  title="Add Note"
                >
                  <FileText className="w-5 h-5" />
                </button>
              </div>
              
              {formattedContents.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formattedContents.map(content => (
                    <div
                      key={content.id}
                      className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
                    >
                      {content.type === 'code' ? <Code2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                      <span>{getFormattedContentPreview(content)}</span>
                      <button
                        type="button"
                        onClick={() => removeFormattedContent(content.id)}
                        className="text-gray-500 hover:text-red-500"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Ask a question..."
                  className="flex-1 p-2 border rounded-lg focus:outline-none focus:border-purple-500 min-h-[40px] max-h-[120px] resize-y"
                  disabled={isLoading}
                  rows={Math.min(3, newMessage.split('\n').length)}
                />
                <button
                  type="submit"
                  disabled={isLoading || (!newMessage.trim() && formattedContents.length === 0)}
                  className="bg-purple-600 text-white p-2 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-purple-300"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}